import React, { useState } from 'react';
import { NavigationProps } from '../types/navigation';
import { Button } from '../components/ui/Button';
import { ArrowLeft, Check, Star, ThumbsUp, ThumbsDown, Package } from 'lucide-react';
import { useStore } from '../lib/store';
type Step = 'intro' | 'product-review' | 'complete';
interface ProductReview {
  taste: boolean | null;
  packaging: boolean | null;
  buyAgain: boolean | null;
}
export function ReviewFlow({
  onNavigate
}: NavigationProps) {
  const [step, setStep] = useState<Step>('intro');
  const [currentProductIndex, setCurrentProductIndex] = useState(0);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [productReviews, setProductReviews] = useState<Record<string, ProductReview>>({});
  const {
    user,
    reservations,
    events,
    completeReview,
    addReviewEntry
  } = useStore();
  const reservationToReview = reservations.find(r => r.status === 'redeemed');
  const event = events.find(e => e.id === reservationToReview?.eventId);
  // Fallback to a default product if none are configured for the drop
  const products = event?.products?.length ? event.products : [{
    name: 'Mystery Catch',
    brand: event?.brand || 'Gone Fishing'
  }];
  const currentProduct = products[currentProductIndex];
  const handleAnswer = (value: boolean) => {
    const questionKeys: (keyof ProductReview)[] = ['taste', 'packaging', 'buyAgain'];
    const currentKey = questionKeys[questionIndex];
    // Update the current product's review
    setProductReviews(prev => ({
      ...prev,
      [currentProduct.name]: {
        ...prev[currentProduct.name],
        [currentKey]: value
      }
    }));
    if (questionIndex < 2) {
      setQuestionIndex(prev => prev + 1);
    } else {
      // Save this product's review to the store
      const review = {
        ...productReviews[currentProduct.name],
        [currentKey]: value
      };
      addReviewEntry({
        reservationId: reservationToReview?.id || '',
        userId: user?.phone || '',
        productName: currentProduct.name,
        productBrand: currentProduct.brand,
        taste: review.taste ?? null,
        packaging: review.packaging ?? null,
        buyAgain: value
      });
      if (currentProductIndex < products.length - 1) {
        setCurrentProductIndex(prev => prev + 1);
        setQuestionIndex(0);
      } else {
        setStep('complete');
      }
    }
  };
  const handleComplete = () => {
    if (reservationToReview) {
      completeReview(reservationToReview.id);
    }
    onNavigate('feed');
  };
  if (!reservationToReview && step !== 'complete') {
    return <div className="min-h-screen flex flex-col items-center justify-center p-6 text-center" data-id="element-2446" data-mp="1s4ol6mmy4ggq">
        <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-2447" data-mp="14f2l7wyr1x7o">
          <Package className="w-10 h-10 text-gray-400" data-id="element-2448" data-mp="10dqshikkwvv9" />
        </div>
        <p className="text-gray-600 font-medium mb-2" data-id="element-2449" data-mp="10bednea4b1mf">
          Nothing to review right now
        </p>
        <p className="text-gray-400 text-sm mb-6" data-id="element-2450" data-mp="q7h6db0eewjz">
          Get a catch first, then you can leave a review.
        </p>
        <Button onClick={() => onNavigate('feed')} variant="secondary" data-id="element-2451" data-mp="gbe1vyi2qltv">
          Back to Feed
        </Button>
      </div>;
  }
  // --- SUB-COMPONENTS ---
  const IntroView = () => <div className="flex flex-col h-full p-6 animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-2452" data-mp="l84hkkpyvfer">
      <div className="flex-1 flex flex-col items-center justify-center space-y-8 text-center" data-id="element-2453" data-mp="1dz4p35nib1b6">
        <div className="w-24 h-24 bg-brand-lavender rounded-full flex items-center justify-center mb-4 shadow-[0_12px_28px_rgba(96,70,232,0.16)]" data-id="element-2454" data-mp="1dws74shr0qy4">
          <Package className="w-12 h-12 text-brand-purple" data-id="element-2455" data-mp="13zxeurc2jmsa" />
        </div>
        <div data-id="element-2456" data-mp="3jrl92ezp717e">
          <h1 className="text-4xl font-display text-brand-navy uppercase tracking-wide mb-2" data-id="element-2457" data-mp="3jrtity0ixqed">
            Time to review!
          </h1>
          <p className="text-gray-500 max-w-xs mx-auto font-medium" data-id="element-2458" data-mp="3oshbsp8bchb8">
            Rate the {products.length} product{products.length !== 1 ? 's' : ''}{' '}
            in your catch to unlock the next drop.
          </p>
        </div>
        <div className="bg-white border border-brand-lavender rounded-[1.5rem] p-5 w-full max-w-xs shadow-[0_10px_24px_rgba(22,38,95,0.06)]" data-id="element-2459" data-mp="3v89dfxuuirvu">
          <p className="text-sm font-bold text-brand-navy uppercase tracking-wider" data-id="element-2460" data-mp="3qar07v9spqi1">
            🔒 Next drop is locked
          </p>
          <p className="text-xs text-gray-500 mt-1" data-id="element-2461" data-mp="rppr1byumg8y">
            Complete all reviews to unlock.
          </p>
        </div>
      </div>
      <Button onClick={() => setStep('product-review')} className="mt-auto w-full" data-id="element-2462" data-mp="ht38wuxwq3am">
        Start Review
      </Button>
    </div>;
  const ProductReviewView = () => {
    const questions = [{
      title: `Did you like the taste of ${currentProduct.name}?`,
      key: 'taste'
    }, {
      title: `What did you think of the packaging?`,
      key: 'packaging'
    }, {
      title: `Would you buy ${currentProduct.name} again?`,
      key: 'buyAgain'
    }];
    const currentQuestion = questions[questionIndex];
    return <div className="flex flex-col h-full p-6 text-center animate-in slide-in-from-right duration-300" key={`${currentProductIndex}-${questionIndex}`} data-id="element-2463" data-mp="1n0dxaigd441v">
        <div className="mb-8" data-id="element-2464" data-mp="1wqpg1b4pghst">
          <span className="text-xs font-bold text-brand-purple uppercase tracking-widest" data-id="element-2465" data-mp="1wrgydk9akj15">
            Product {currentProductIndex + 1} of {products.length}
          </span>
          <h2 className="text-3xl font-display text-brand-navy uppercase tracking-wide mt-2" data-id="element-2466" data-mp="25rn9izhip6x5">
            {currentProduct.brand}
          </h2>
          <p className="text-gray-500" data-id="element-2467" data-mp="2ap5n536hcabh">{currentProduct.name}</p>
        </div>

        <div className="flex-1 flex flex-col justify-center" data-id="element-2468" data-mp="be5wi6eqnsgd">
          <h3 className="text-xl font-bold mb-8" data-id="element-2469" data-mp="gcfq4cktekzv">{currentQuestion.title}</h3>
          <div className="grid grid-cols-2 gap-4" data-id="element-2470" data-mp="gcpejvq7ok3g">
            <button onClick={() => handleAnswer(false)} className="p-6 rounded-[1.5rem] border-2 flex flex-col items-center justify-center space-y-2 border-white bg-white text-brand-navy/45 hover:border-red-300 hover:bg-red-50 hover:text-red-600 shadow-[0_8px_22px_rgba(22,38,95,0.05)] transition-all active:scale-95" data-id="element-2471" data-mp="8azp0sbgz5ab">
              <ThumbsDown className="w-8 h-8" data-id="element-2472" data-mp="dd77m0qt1jlm" />
              <span className="font-bold" data-id="element-2473" data-mp="r9kdeclz7wk9">No</span>
            </button>
            <button onClick={() => handleAnswer(true)} className="p-6 rounded-[1.5rem] border-2 flex flex-col items-center justify-center space-y-2 border-white bg-white text-brand-navy/45 hover:border-green-300 hover:bg-green-50 hover:text-green-600 shadow-[0_8px_22px_rgba(22,38,95,0.05)] transition-all active:scale-95" data-id="element-2474" data-mp="n9a68wmg703t">
              <ThumbsUp className="w-8 h-8" data-id="element-2475" data-mp="1awicbgrpad6w" />
              <span className="font-bold" data-id="element-2476" data-mp="1601m8h0xxor6">Yes</span>
            </button>
          </div>
        </div>

        <div className="mt-auto pt-6" data-id="element-2477" data-mp="1pl1wqwx84ihd">
          <div className="w-full bg-gray-100 h-2 rounded-full overflow-hidden" data-id="element-2478" data-mp="1pm3872i5j6zm">
            <div className="bg-brand-purple h-full transition-all duration-300" style={{
            width: `${(currentProductIndex * 3 + questionIndex + 1) / (products.length * 3) * 100}%`
          }} data-id="element-2479" data-mp="1tllua1lplo41"></div>
          </div>
          <p className="text-xs text-gray-400 mt-2" data-id="element-2480" data-mp="15x2ktiqxb5ad">
            Question {currentProductIndex * 3 + questionIndex + 1} of{' '}
            {products.length * 3}
          </p>
        </div>
      </div>;
  };
  const CompleteView = () => <div className="flex flex-col h-full p-6 animate-in zoom-in duration-500" data-id="element-2481" data-mp="wyh65yokoaev">
      <div className="flex-1 flex flex-col items-center justify-center text-center space-y-6" data-id="element-2482" data-mp="11wiouii367pt">
        <div className="w-24 h-24 bg-brand-lavender rounded-full flex items-center justify-center mb-2" data-id="element-2483" data-mp="mzhxwh7pygse">
          <span className="text-4xl" data-id="element-2484" data-mp="rw8bmxw1x0an">🔓</span>
        </div>
        <div data-id="element-2485" data-mp="17fawiu7eben7">
          <h1 className="text-4xl font-display text-brand-navy uppercase tracking-wide mb-2" data-id="element-2486" data-mp="17fuh6iofkh4x">
            Next Drop Unlocked!
          </h1>
          <p className="text-gray-500" data-id="element-2487" data-mp="13er05suncf24">
            Thanks for your feedback! You can now see the next event in your
            Feed.
          </p>
        </div>
        <div className="bg-brand-lavender text-brand-purple px-4 py-2 rounded-full font-bold text-sm" data-id="element-2488" data-mp="tgkztfohngzd">
          +30 Points Earned
        </div>
      </div>
      <Button onClick={handleComplete} className="mt-auto w-full" data-id="element-2489" data-mp="oijjh2ig5vrl">
        Show Next Drop
      </Button>
    </div>;
  return <div className="min-h-screen bg-brand-ice relative overflow-hidden flex flex-col" data-id="element-2490" data-mp="fnvcn7x8460o">
      {step !== 'intro' && step !== 'complete' && <div className="p-4 flex items-center" data-id="element-2491" data-mp="aqv851g0vbbo">
          <button onClick={() => onNavigate('catches')} className="p-2 hover:bg-gray-100 rounded-full" data-id="element-2492" data-mp="5pxpyov0iha6">
            <ArrowLeft className="w-6 h-6 text-gray-700" data-id="element-2493" data-mp="sfdwplp7lxy" />
          </button>
        </div>}
      <div className="flex-1" data-id="element-2494" data-mp="3mrs7nhnc3isy">
        {step === 'intro' && <IntroView data-id="element-2495" data-mp="3rrodznji7mbo" />}
        {step === 'product-review' && <ProductReviewView data-id="element-2496" data-mp="3rq3fjmu2jj8j" />}
        {step === 'complete' && <CompleteView data-id="element-2497" data-mp="lmxv0kom2j6" />}
      </div>
    </div>;
}